import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Container, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Grid, Card, CardContent,
  CircularProgress, IconButton, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, TextField, InputAdornment
} from "@material-ui/core";
import {
  Refresh as RefreshIcon,
  CloudQueue as CloudIcon,
  FolderOpen as FolderOpenIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  PlayArrow as PlayIcon,
  Schedule as ScheduleIcon
} from "@material-ui/icons";
import api from "../../services/api";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  filterSection: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  cardStorage: {
    marginBottom: theme.spacing(3),
    backgroundColor: "#f8f9fa",
  },
  tableHeader: {
    backgroundColor: "#34495e",
  },
  headerCell: {
    color: "#fff",
    fontWeight: "bold",
  },
}));

export default function BackupStatus() {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [executingScript, setExecutingScript] = useState(false);
  const [backups, setBackups] = useState([]);
  const [storage, setStorage] = useState(null);
  const [lastExecution, setLastExecution] = useState(null);

  // Estado de Busca da Tabela Principal
  const [searchTerm, setSearchTerm] = useState("");

  // Estados do Modal e Busca no Modal
  const [openModal, setOpenModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState("");
  const [clientFiles, setClientFiles] = useState([]);
  const [fileSearchTerm, setFileSearchTerm] = useState("");
  const [loadingFiles, setLoadingFiles] = useState(false);

const fetchBackupData = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/backups-status");
      
      const backupsList = Array.isArray(data) ? data : (data.backups || []);
      setBackups(backupsList);

      // Tratamento do storage (se o backend mandar array ou objeto)
      const storageObj = Array.isArray(data.storage) ? data.storage[0] : data.storage;
      setStorage(storageObj || null);

      // Pega a data_execucao de dentro do objeto storage
      if (storageObj && storageObj.data_execucao) {
        setLastExecution(storageObj.data_execucao);
      } else if (data.data_execucao) {
        setLastExecution(data.data_execucao);
      }
    } catch (err) {
      toast.error("Erro ao carregar status dos backups.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBackupData();
  }, []);

  // Função para executar o script de backup no servidor
  const handleRunBackupScript = async () => {
    setExecutingScript(true);
    try {
      const { data } = await api.post("/backups-status/executar-script");
      toast.success(data.message || "Script de backup executado com sucesso!");
      // Atualiza a listagem automaticamente após rodar o script
      fetchBackupData();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Erro ao executar o script no servidor.");
    } finally {
      setExecutingScript(false);
    }
  };

  // Abrir modal e buscar arquivos do cliente
  const handleOpenClientFiles = async (cliente) => {
    setSelectedClient(cliente);
    setOpenModal(true);
    setLoadingFiles(true);
    setFileSearchTerm(""); // Limpa busca do modal

    try {
      const { data } = await api.get(`/backups-status/cliente/${encodeURIComponent(cliente)}`);
      setClientFiles(data || []);
    } catch (err) {
      toast.error("Erro ao carregar arquivos do cliente.");
    } finally {
      setLoadingFiles(false);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setClientFiles([]);
    setSelectedClient("");
    setFileSearchTerm("");
  };

  const getStatusChip = (status) => {
    if (!status) return null;
    if (status.includes("EM DIA")) return <Chip label={status} style={{ backgroundColor: "#2ecc71", color: "#fff" }} size="small" />;
    if (status.includes("INCOMPLETO")) return <Chip label={status} style={{ backgroundColor: "#f39c12", color: "#fff" }} size="small" />;
    return <Chip label={status} style={{ backgroundColor: "#e74c3c", color: "#fff" }} size="small" />;
  };

  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // Filtro de Clientes (Tabela Principal)
  const filteredBackups = backups.filter((item) =>
    item.cliente.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Filtro de Arquivos (Tabela do Modal)
  const filteredFiles = clientFiles.filter((file) =>
    file.nome_arquivo.toLowerCase().includes(fileSearchTerm.toLowerCase())
  );

  return (
    <Container maxWidth="lg" className={classes.container}>
      <div className={classes.header}>
        <Typography variant="h5" style={{ fontWeight: "bold", display: "flex", alignItems: "center", gap: 8 }}>
          <CloudIcon color="primary" /> Monitoramento de Backups dos Clientes
        </Typography>

        {/* AÇÕES DO CABEÇALHO (BOTÃO DE EXECUÇÃO + REFRESH) */}
        <div className={classes.headerActions}>
          <Button
            variant="contained"
            color="primary"
            disabled={executingScript || loading}
            onClick={handleRunBackupScript}
            startIcon={
              executingScript ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <PlayIcon />
              )
            }
          >
            {executingScript ? "Executando..." : "Rodar Backup Agora"}
          </Button>

          <Tooltip title="Atualizar dados">
            <IconButton onClick={fetchBackupData} color="primary" disabled={loading || executingScript}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      {/* PAINEL DE STORAGE E ÚLTIMA EXECUÇÃO */}
      <Grid container spacing={2} className={classes.cardStorage}>
        {storage && (
          <>
            <Grid item xs={12} sm={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>Total Storage</Typography>
                  <Typography variant="h6">{storage.total_bytes || "N/A"}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>Espaço Usado</Typography>
                  <Typography variant="h6">{storage.usado_bytes || "N/A"}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>Espaço Livre</Typography>
                  <Typography variant="h6" style={{ color: storage.status === "OK" ? "#27ae60" : "#c0392b" }}>
                    {storage.livre_bytes || "N/A"} ({storage.status})
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </>
        )}

        {/* CARD COM A ÚLTIMA EXECUÇÃO REGISTRADA */}
        <Grid item xs={12} sm={storage ? 3 : 12}>
          <Card variant="outlined" style={{ backgroundColor: "#eef2f5" }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <ScheduleIcon fontSize="small" /> Última Verificação
              </Typography>
              <Typography variant="h6" style={{ fontSize: "1rem", fontWeight: "bold" }}>
                {lastExecution
                  ? new Date(lastExecution).toLocaleString("pt-BR")
                  : "Não informada"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* BARRA DE PESQUISA DA TABELA PRINCIPAL */}
      <div className={classes.filterSection}>
        <TextField
          variant="outlined"
          size="small"
          placeholder="Pesquisar cliente ou pasta..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ width: 320, backgroundColor: "#fff" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
        <Typography variant="body2" color="textSecondary">
          Exibindo {filteredBackups.length} de {backups.length} clientes
        </Typography>
      </div>

      {/* TABELA PRINCIPAL DE CLIENTES */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead className={classes.tableHeader}>
            <TableRow>
              <TableCell className={classes.headerCell}>Cliente / Pasta</TableCell>
              <TableCell align="center" className={classes.headerCell}>Arquivos</TableCell>
              <TableCell align="center" className={classes.headerCell}>Tamanho (Último)</TableCell>
              <TableCell align="center" className={classes.headerCell}>Último Backup</TableCell>
              <TableCell align="center" className={classes.headerCell}>Atraso (Dias)</TableCell>
              <TableCell align="center" className={classes.headerCell}>Status</TableCell>
              <TableCell align="center" className={classes.headerCell}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" style={{ padding: 24 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredBackups.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" style={{ padding: 24 }}>
                  <Typography color="textSecondary">
                    {searchTerm ? "Nenhum cliente encontrado para essa pesquisa." : "Nenhum registro encontrado."}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredBackups.map((row) => (
                <TableRow key={row.cliente} hover>
                  <TableCell style={{ fontWeight: 600 }}>{row.cliente}</TableCell>
                  <TableCell align="center">{row.total_arquivos_guardados}</TableCell>
                  <TableCell align="center">{formatBytes(row.tamanho_bytes)}</TableCell>
                  <TableCell align="center">
                    {row.data_ultimo_backup ? new Date(row.data_ultimo_backup).toLocaleString("pt-BR") : "N/A"}
                  </TableCell>
                  <TableCell align="center">{row.dias_atrasado ?? "-"}</TableCell>
                  <TableCell align="center">{getStatusChip(row.status_atual)}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Ver Arquivos">
                      <IconButton 
                        color="primary" 
                        size="small"
                        onClick={() => handleOpenClientFiles(row.cliente)}
                      >
                        <FolderOpenIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* MODAL DE LISTAGEM DOS ARQUIVOS DO CLIENTE */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>📂 Arquivos do Cliente: <strong>{selectedClient}</strong></span>
          <IconButton size="small" onClick={handleCloseModal}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {/* BUSCA NO MODAL */}
          <div style={{ marginBottom: 16 }}>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Buscar arquivo específico..."
              value={fileSearchTerm}
              onChange={(e) => setFileSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </div>

          {loadingFiles ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
              <CircularProgress />
            </div>
          ) : filteredFiles.length === 0 ? (
            <Typography align="center" color="textSecondary" style={{ padding: 16 }}>
              {fileSearchTerm ? "Nenhum arquivo encontrado para essa pesquisa." : "Nenhum arquivo encontrado nesta pasta."}
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead style={{ backgroundColor: "#f5f5f5" }}>
                  <TableRow>
                    <TableCell style={{ fontWeight: "bold" }}>Nome do Arquivo</TableCell>
                    <TableCell align="center" style={{ fontWeight: "bold" }}>Tamanho</TableCell>
                    <TableCell align="center" style={{ fontWeight: "bold" }}>Data Modificação</TableCell>
                    <TableCell align="center" style={{ fontWeight: "bold" }}>Data Verificação</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredFiles.map((file) => (
                    <TableRow key={file.id} hover>
                      <TableCell>{file.nome_arquivo}</TableCell>
                      <TableCell align="center">{formatBytes(file.tamanho_bytes)}</TableCell>
                      <TableCell align="center">
                        {file.data_modificacao ? new Date(file.data_modificacao).toLocaleString("pt-BR") : "N/A"}
                      </TableCell>
                      <TableCell align="center">
                        {file.data_verificacao ? new Date(file.data_verificacao).toLocaleString("pt-BR") : "N/A"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="primary" variant="contained">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}